import { isEmpty, isNumber } from 'lodash';
import React, { FunctionComponent, createContext, useContext, useRef, useReducer } from 'react';
import { showErrorNotification } from 'src/components/widgets/notificationToaster';

type ValidationMessageValue<TValue> = string | ((value: TValue) => string);

interface configurationInterface<T> {
    key: string;
    validate: (value: T) => boolean;
    message: string;
}

export interface ValidationControlProps<TValue = unknown> {
    validationKey?: string;
    validate?: (value: TValue) => boolean;
    validationMessage?: ValidationMessageValue<TValue>;
    message?: ValidationMessageValue<TValue>;
    validationLabel?: string;
    validationValue?: TValue;
}

interface ValidationFieldMetadata {
    label?: string;
    element?: HTMLElement | null;
    order?: number;
}

interface ValidationError {
    validationKey: string;
    message: string;
    label?: string;
    element?: HTMLElement | null;
    order: number;
}

interface SubmitValidationOptions {
    scroll?: boolean;
    focus?: boolean;
    showNotification?: boolean;
}

export const validateRequired = (value: string | number, zeroIsAllowed: boolean = true) => {
    if (!zeroIsAllowed && +value === 0) return true;
    else if (zeroIsAllowed && isNumber(value)) return false;
    else return isEmpty(value);
};

export const validateRequiredTrimmed = (value: string | number | null | undefined, zeroIsAllowed: boolean = true) => {
    if (!zeroIsAllowed && Number(value) === 0) return true;
    if (zeroIsAllowed && isNumber(value)) return false;
    return value == null || value.toString().trim().length === 0;
};

export const validateEmail = (value: string) => {
    //pattern derived from stringHelper cs
    return !/^(?:[a-z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+\/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)$/i.test(
        value
    );
};

export const validatePassword = (value: string) => {
    //pattern derived from stringHelper cs
    return !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d_\-\+\\=\/@\.!#$%&\'\*\+\;\[\]\,\(\)\{\}\^\°\~\`\:]{8,}$/.test(value);
};

export const validateUrl = (value: string): boolean => {
    if (!value) return true;
    const protocolRegex = /^(https?:\/\/)/;
    const urlRegex =
        /^https?:\/\/((([a-z\d]([a-z\d-]*[a-z\d])*)\.)+[a-z]{2,}|((\d{1,3}\.){3}\d{1,3}))(:\d+)?(\/[-a-z\d%_.~+]*)*(\?[;&a-z\d%_.~+=-]*)?(\#[-a-z\d_]*)?$/i;

    return !(protocolRegex.test(value) || urlRegex.test(value));
};

export const isStepAligned = (value: number, step: number): boolean => {
    if (isNumber(value) && isNumber(step) && step > 0) {
        const epsilon = 1e-10;
        const nearest = Math.round(value / step) * step;
        return Math.abs(nearest - value) < epsilon;
    }
    return true;
};

export function useValidation<T>(configurations: configurationInterface<T>[]) {
    const _validationContext = useContext(validationContext);

    const clearValidation = () => {
        if (_validationContext.update && configurations.length > 0) _validationContext.update(configurations[0].key, undefined);
    };

    return {
        check: (value: T) => {
            // Clear validation
            clearValidation();
            const validations = configurations.filter((configuration) => {
                if (configuration.validate) return configuration.validate(value);
                return false;
            });

            if (_validationContext.update && validations.length > 0) _validationContext.update(validations[0].key, validations[0].message);
        },
        remove: () => {
            if (_validationContext.remove && configurations.length > 0) _validationContext.remove(configurations[0].key);
        }
    };
}

interface ValidationContextInterface {
    update: (validationKey: string, message: string | null | undefined) => void;
    remove: (validationKey: string) => void;
    clear: () => void;
    isValid: () => boolean;
    hasError: (validationKey: string) => boolean;
    hasSubmitted: () => boolean;
    resetSubmitted: () => void;
    submit: (options?: SubmitValidationOptions) => boolean;
    registerField: (validationKey: string, metadata: ValidationFieldMetadata) => void;
    unregisterField: (validationKey: string) => void;
    getErrors: () => ValidationError[];
    validations: { validationKey: string; message: string | null | undefined }[];
}

export const validationContext = createContext<ValidationContextInterface>({
    update: (validationKey: string, message: string | null | undefined) => {},
    remove: (validationKey: string) => {},
    clear: () => {},
    isValid: () => true,
    hasError: (validationKey: string) => false,
    hasSubmitted: () => false,
    resetSubmitted: () => {},
    submit: () => true,
    registerField: (validationKey: string, metadata: ValidationFieldMetadata) => {},
    unregisterField: (validationKey: string) => {},
    getErrors: () => [],
    validations: []
});

export const useValidationContext = () => useContext(validationContext);

export const useValidationControl = <TElement extends HTMLElement, TValue = unknown>(
    props: ValidationControlProps<TValue>,
    value: TValue,
    externalRef?: React.Ref<TElement>
) => {
    const validationContext = useValidationContext();
    const controlRef = useRef<TElement | null>(null);
    const hasValidation = !!props.validationKey && !!props.validate;
    const valueToValidate = (props.validationValue ?? value) as TValue;
    const message = props.validationMessage ?? props.message ?? '';
    const resolvedMessage = typeof message === 'function' ? message(valueToValidate) : message;

    const validation = useValidation<TValue>(
        hasValidation
            ? [
                  {
                      key: props.validationKey!,
                      validate: props.validate!,
                      message: resolvedMessage
                  }
              ]
            : []
    );

    if (hasValidation) validation.check(valueToValidate);

    const setRef = React.useCallback(
        (element: TElement | null) => {
            controlRef.current = element;

            if (typeof externalRef === 'function') {
                externalRef(element);
            } else if (externalRef) {
                (externalRef as React.MutableRefObject<TElement | null>).current = element;
            }
        },
        [externalRef]
    );

    React.useEffect(() => {
        if (!props.validationKey) return;

        validationContext.registerField(props.validationKey, {
            label: props.validationLabel,
            element: controlRef.current
        });

        return () => validationContext.unregisterField(props.validationKey!);
    }, [props.validationKey, props.validationLabel, validationContext]);

    return {
        isValidationError: Boolean(hasValidation && props.validate!(valueToValidate) && validationContext.hasSubmitted()),
        validationKey: props.validationKey,
        setRef
    };
};

export const ValidationStateProvider: FunctionComponent<{ children?: React.ReactNode }> = ({ children }) => {
    const [, forceRender] = useReducer((value: number) => value + 1, 0);
    const fields = useRef<Record<string, ValidationFieldMetadata & { order: number }>>({});
    const submitAttempted = useRef(false);
    const nextOrder = useRef(0);

    const refreshContext = () => {
        validationState.current = { ...validationState.current };
        forceRender();
    };

    const getErrors = (): ValidationError[] => {
        return validationState.current.validations
            .filter((validation) => validation.message != null)
            .map((validation) => {
                const field = fields.current[validation.validationKey];
                return {
                    validationKey: validation.validationKey,
                    message: validation.message || '',
                    label: field?.label,
                    element: field?.element,
                    order: field?.order ?? Number.MAX_SAFE_INTEGER
                };
            })
            .sort((a, b) => a.order - b.order);
    };

    const getFocusableElement = (element?: HTMLElement | null) => {
        if (!element) return undefined;

        if (typeof element.focus === 'function' && !element.hasAttribute('disabled')) return element;

        return element.querySelector<HTMLElement>(
            'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
    };

    const scrollToError = (error: ValidationError, focus: boolean) => {
        const element = error.element;
        if (!element) return;

        element.scrollIntoView({ behavior: 'smooth', block: 'center' });

        if (focus) {
            const focusable = getFocusableElement(element);
            setTimeout(() => focusable?.focus({ preventScroll: true }), 250);
        }
    };

    const showValidationNotification = (errors: ValidationError[]) => {
        const values = errors.map((error) => error.message.trim()).filter(Boolean);
        const uniqueValues = Array.from(new Set(values));

        showErrorNotification({
            message: (
                <>
                    {uniqueValues.map((value) => (
                        <div key={value}>{value}</div>
                    ))}
                </>
            )
        });
    };

    const validationState: React.MutableRefObject<ValidationContextInterface> = useRef<ValidationContextInterface>({
        update: (validationKey: string, message: string | null | undefined) => {
            // i componenti figli aggiornano il contesto
            if (validationState.current.validations) {
                const validations = [
                    ...validationState.current.validations.filter((v) => v.validationKey != validationKey),
                    { validationKey: validationKey, message: message }
                ];
                validationState.current.validations = validations;
            }
        },
        // Allow to remove all validations with a specific prefix
        remove: (validationKeyPrefix: string) => {
            if (validationState.current.validations) {
                const validations = [...validationState.current.validations.filter((v) => !v.validationKey.startsWith(validationKeyPrefix))];
                validationState.current.validations = validations;
            }

            Object.keys(fields.current)
                .filter((key) => key.startsWith(validationKeyPrefix))
                .forEach((key) => delete fields.current[key]);
        },
        clear: () => {
            validationState.current.validations = [];
            submitAttempted.current = false;
            refreshContext();
        },
        isValid: (): boolean => {
            return !validationState.current.validations.some((x) => x.message != null);
        },
        hasError: (validationKey: string): boolean => {
            return validationState.current.validations.some((x) => x.validationKey === validationKey && x.message != null);
        },
        hasSubmitted: () => submitAttempted.current,
        resetSubmitted: () => {
            submitAttempted.current = false;
            refreshContext();
        },
        submit: (options?: SubmitValidationOptions) => {
            submitAttempted.current = true;
            const errors = getErrors();

            if (errors.length > 0) {
                if (options?.scroll !== false) scrollToError(errors[0], options?.focus !== false);
                if (options?.showNotification !== false) showValidationNotification(errors);
                refreshContext();
                return false;
            }

            refreshContext();
            return true;
        },
        registerField: (validationKey: string, metadata: ValidationFieldMetadata) => {
            const previous = fields.current[validationKey];
            fields.current[validationKey] = {
                ...previous,
                ...metadata,
                order: metadata.order ?? previous?.order ?? nextOrder.current++
            };
        },
        unregisterField: (validationKey: string) => {
            delete fields.current[validationKey];
            validationState.current.validations = validationState.current.validations.filter((validation) => validation.validationKey !== validationKey);
        },
        getErrors,
        validations: []
    });

    return <validationContext.Provider value={validationState.current}> {children} </validationContext.Provider>;
};
